/**
 * @fileoverview System uploaded file service — local-first CRUD for system-scoped uploaded files.
 * @module services/systemUploadedFileService
 *
 * Responsibilities:
 * - CRUD operations for system uploaded files in Dexie.
 * - File replacement (new blob URL).
 * - Download URL retrieval (inherently-online exception).
 * - Audit logging for all mutations.
 *
 * Dependencies:
 * - @firesquirrel/local-db (getSystemDb, wakeLocalWrite)
 * - packages/audit (runAuditedMutation)
 * - ./systemAuditLogService (writeAuditLog)
 * - ../apiClient (apiFetch — only for download URL)
 *
 * Architecture:
 * - Local-first: Dexie is source of truth; background sync reconciles with MongoDB.
 * - Download URL is inherently-online (server-issued SAS URL).
 *
 * @localFirst
 * Source of Truth: local (Dexie)
 * Write Flow: Dexie write → wakeLocalWrite → sync push to MongoDB
 * Sync Strategy: Optimistic write; background push
 * Conflict Resolution: Last-write-wins (server timestamp)
 *
 * Notes:
 * - replaceUploadedFile delegates to updateUploadedFile and is audited through that path.
 */

import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import type { UploadedFileLocal } from '@firesquirrel/local-db';
import { runAuditedMutation } from '../../../packages/audit/src';
import { writeAuditLog } from './systemAuditLogService';
import { apiFetch } from '../apiClient';

export interface ListUploadedFilesParams {
  category?: 'pamphlet' | 'instruction' | 'feeSchedule' | 'letterhead' | 'other';
  mimeType?: string;
  tags?: string[];
}

export async function listUploadedFiles(
  params?: ListUploadedFilesParams,
): Promise<UploadedFileLocal[]> {
  const db = await getSystemDb();
  const results = await db.uploaded_files.toArray();
  let items = results as UploadedFileLocal[];

  if (params?.category) {
    items = items.filter((item) => item.category === params.category);
  }

  if (params?.mimeType) {
    items = items.filter((item) => item.mimeType === params.mimeType);
  }

  if (params?.tags && params.tags.length > 0) {
    items = items.filter((item) => 
      item.tags?.some(tag => params.tags!.includes(tag))
    );
  }

  return items.sort((a, b) => {
    const aDate = new Date(a.updatedAt || a.createdAt).getTime();
    const bDate = new Date(b.updatedAt || b.createdAt).getTime();
    return bDate - aDate;
  });
}

export async function getUploadedFile(id: string): Promise<UploadedFileLocal | null> {
  const db = await getSystemDb();
  const doc = await db.uploaded_files.get(id);
  return (doc as UploadedFileLocal | undefined) ?? null;
}

/** @process createUploadedFile — Dexie mutation + audit entry. */
export async function createUploadedFile(
  data: Partial<UploadedFileLocal>,
): Promise<UploadedFileLocal> {
  const db = await getSystemDb();
  const now = new Date().toISOString();
  const newId = data._id || `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  return runAuditedMutation<null, UploadedFileLocal>({
    collection: 'uploadedFiles',
    documentId: newId,
    action: 'create',
    summary: `Uploaded system file: ${(data as any).fileName ?? newId}`,
    getBefore: async () => null,
    execute: async () => {
      const newDoc = {
        ...data,
        _id: newId,
        createdAt: now,
        updatedAt: now,
      };
      await db.uploaded_files.put(newDoc as any);
      wakeLocalWrite('uploaded_files');
      return newDoc as UploadedFileLocal;
    },
    getAfter: async () => (await db.uploaded_files.get(newId)) as UploadedFileLocal | null,
    writeAuditLog,
  });
}

/** @process updateUploadedFile — Dexie mutation + audit entry. */
export async function updateUploadedFile(
  id: string,
  patch: Partial<UploadedFileLocal>,
): Promise<UploadedFileLocal | null> {
  const db = await getSystemDb();
  const existing = await db.uploaded_files.get(id);
  if (!existing) return null;

  return runAuditedMutation<UploadedFileLocal, UploadedFileLocal>({
    collection: 'uploadedFiles',
    documentId: id,
    action: 'update',
    summary: `Updated system file: ${(existing as any).fileName ?? id}`,
    getBefore: async () => (await db.uploaded_files.get(id)) as UploadedFileLocal | null,
    execute: async () => {
      const updated = { ...(existing as any), ...patch, updatedAt: new Date().toISOString() };
      await db.uploaded_files.put(updated);
      wakeLocalWrite('uploaded_files');
      return updated as UploadedFileLocal;
    },
    getAfter: async () => (await db.uploaded_files.get(id)) as UploadedFileLocal | null,
    writeAuditLog,
  });
}

/** @process deleteUploadedFile — Dexie mutation + audit entry. */
export async function deleteUploadedFile(id: string): Promise<boolean> {
  const db = await getSystemDb();
  const existing = await db.uploaded_files.get(id);
  if (!existing) return false;

  await runAuditedMutation<UploadedFileLocal, UploadedFileLocal>({
    collection: 'uploadedFiles',
    documentId: id,
    action: 'delete',
    summary: `Deleted system file: ${(existing as any).fileName ?? id}`,
    getBefore: async () => (await db.uploaded_files.get(id)) as UploadedFileLocal | null,
    execute: async () => {
      const tombstone = { ...(existing as any), _deleted: true, deletedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      await db.uploaded_files.put(tombstone);
      wakeLocalWrite('uploaded_files');
      return tombstone as UploadedFileLocal;
    },
    getAfter: async () => (await db.uploaded_files.get(id)) as UploadedFileLocal | null,
    writeAuditLog,
  });

  return true;
}

/** @process replaceUploadedFile — delegates to audited mutation. */
export async function replaceUploadedFile(
  id: string,
  newBlobUrl: string,
  newFileName: string,
  newSizeBytes: number,
): Promise<UploadedFileLocal | null> {
  return updateUploadedFile(id, {
    blobUrl: newBlobUrl,
    fileName: newFileName,
    sizeBytes: newSizeBytes,
  });
}

export async function getUploadedFileDownloadUrl(id: string): Promise<string> {
  // Inherently-online exception: blob access requires a short-lived server-issued
  // SAS URL and cannot be sourced from Dexie.
  const data = await apiFetch(`/api/files/${encodeURIComponent(id)}/download`) as { downloadUrl?: string };
  if (!data.downloadUrl) {
    throw new Error('No download URL returned');
  }
  return data.downloadUrl;
}
